<template>
    <div
        class="purchase-invoice-upload"
        :class="{
            'is-uploading': isUploading,
            'is-dragging': isDragging,
        }"
    >
        <input
            ref="fileInput"
            type="file"
            class="purchase-invoice-upload__input"
            multiple
            :accept="mimeTypes"
            :disabled="isUploading"
            @change="handleFileChange"
        />

        <div
            class="purchase-invoice-upload__dropzone"
            @dragover.prevent="handleDragOver"
            @dragenter.prevent="handleDragEnter"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="triggerFileDialog"
        >
            <div v-if="isUploading" class="purchase-invoice-upload__dropzone-loader">
                <span class="typographies-paragraph-medium-semibold">{{
                    $t.__('purchases.PurchaseInvoiceUpload.loading')
                }}</span>
                <CompassLoader size="large" />
                <span class="purchase-invoice-upload__dropzone-loader__counter">
                    {{ currentUploadPosition }}/{{ uploadQueueSize }}
                </span>
            </div>
            <div
                v-else
                class="purchase-invoice-upload__dropzone-content"
                :aria-hidden="isUploading ? 'true' : 'false'"
            >
                <p class="typographies-paragraph-medium-semibold m-0">
                    {{ $t.__('purchases.PurchaseInvoiceUpload.instructions') }}
                </p>
                <p
                    class="purchase-invoice-upload__dropzone-content__limit typographies-paragraph-small-regular gray-60 m-0 mt-1"
                >
                    {{ $t.__('purchases.PurchaseInvoiceUpload.constraints') }}
                </p>
                <CompassButton
                    :label="$t.__('ocr.UploadForm.action.import')"
                    leftIcon="upload"
                    type="tertiary"
                    size="medium"
                    class="mt-3"
                    :disabled="isUploading"
                    data-bot="purchases-purinvoices-list_import-invoice_popup"
                    @click.stop="triggerFileDialog"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

const corpMapState = createNamespacedHelpers('corp').mapState;

export default {
    name: 'PurchaseInvoiceUpload',
    props: {
        isUploading: {
            type: Boolean,
            default: false,
        },
        currentUploadPosition: {
            type: Number,
            default: 0,
        },
        uploadQueueSize: {
            type: Number,
            default: 0,
        },
        maxFileSize: {
            type: Number,
            default: 5242880,
        },
    },
    emits: ['files-added'],
    data() {
        return {
            isDragging: false,
        };
    },
    computed: {
        ...corpMapState({
            accountLevel: 'accountLevel',
        }),
        mimeTypes() {
            return 'image/jpeg, image/png, image/webp, application/pdf';
        },
        acceptedMimeTypes() {
            return this.mimeTypes.replace(/\s/g, '').split(',');
        },
    },
    methods: {
        triggerFileDialog() {
            if (this.isUploading) {
                return;
            }
            this.$refs.fileInput.click();
        },
        handleDragEnter() {
            if (this.isUploading) {
                return;
            }
            this.isDragging = true;
        },
        handleDragOver() {
            if (this.isUploading) {
                return;
            }
            this.isDragging = true;
        },
        handleDragLeave() {
            this.isDragging = false;
        },
        handleDrop(event) {
            if (this.isUploading) {
                return;
            }
            const { files } = event.dataTransfer;
            this.isDragging = false;
            if (files?.length) {
                this.addFiles(files);
            }
        },
        handleFileChange(event) {
            if (event?.target?.files?.length) {
                this.addFiles(event.target.files);
            }
            event.target.value = '';
        },
        addFiles(fileList) {
            const newFiles = [];

            for (const file of fileList) {
                const baseFile = {
                    id: `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    name: file.name,
                    size: file.size,
                    file,
                    status: 'pending',
                    errorMessage: '',
                };

                if (!this.acceptedMimeTypes.includes(file.type)) {
                    newFiles.push({
                        ...baseFile,
                        status: 'error',
                        errorMessage: this.$t.__(
                            'purchases.PurchaseInvoiceUpload.error.invalid_format',
                        ),
                    });
                    continue;
                }
                if (file.size > this.maxFileSize) {
                    newFiles.push({
                        ...baseFile,
                        status: 'error',
                        errorMessage: this.$t.__('purchases.PurchaseInvoiceUpload.error.too_large'),
                    });
                    continue;
                }

                newFiles.push(baseFile);
            }

            if (newFiles.length) {
                this.$emit('files-added', newFiles);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.purchase-invoice-upload {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    border-radius: var(--spacing-1);
    border: 1px dashed var(--colors-common-border-lighter);
    padding: var(--spacing-4);
    transition:
        border-color 0.2s ease,
        background-color 0.2s ease;
    &:not(.is-uploading) {
        &:hover,
        &.is-dragging {
            cursor: pointer;
            background-color: var(--colors-common-background-primary-subtle);
            border-color: var(--colors-common-border-informative);
        }
    }

    &__input {
        display: none;
    }
    &__dropzone {
        text-align: center;
    }

    &__dropzone-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        &__limit {
            color: var(--colors-common-text-subtle-default);
            margin-bottom: var(--spacing-3);
        }
    }

    &__dropzone-loader {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-2);
    }

    &__dropzone-loader__counter {
        color: var(--colors-common-text-standard-default);
    }
}
</style>
